import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── BrandSelect ──────────────────────────────────────────────────────────────
// Props:
//   brands       – full brands array from parent
//   vehicleType  – 'car' | 'bike' | 'commercial' | 'ev'
//   evSubtype    – 'car' | 'bike' | 'commercial' | '' (only for vehicleType==='ev')
//   value        – selected brand id
//   onChange     – (id) => void
//
// EV filtering logic:
//   EV, no subtype  → show nothing (disabled)
//   EV + subtype    → show brands where category==='ev'
//                     AND (ev_subtypes includes evSubtype  ← new multi-value array
//                          OR sub_category === evSubtype  ← legacy single value
//                          OR brand has neither field     ← fallback: show all EV brands)
export const BrandSelect = ({ brands, vehicleType, evSubtype, value, onChange }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen]   = useState(false);
  const ref = useRef(null);

  const filtered = brands.filter(b => {
    const matchSearch = !search || b.name?.toLowerCase().includes(search.toLowerCase());

    if (vehicleType === 'ev') {
      if (!evSubtype) return false;           // must choose subtype first
      if (b.category !== 'ev') return false;

      // ── Multi-value field (new): ev_subtypes is string[]
      if (Array.isArray(b.ev_subtypes) && b.ev_subtypes.length > 0) {
        return b.ev_subtypes.includes(evSubtype) && matchSearch;
      }
      // ── Legacy single-value field: sub_category
      if (b.sub_category) {
        return b.sub_category === evSubtype && matchSearch;
      }
      // ── Fallback: brand has no sub-category tagging → show for any EV subtype
      return matchSearch;
    }

    // Non-EV: simple category match
    if (!vehicleType) return false;
    return b.category?.toLowerCase() === vehicleType?.toLowerCase() && matchSearch;
  });

  const selected = brands.find(b => b.id === value);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const getPlaceholder = () => {
    if (vehicleType === 'ev' && !evSubtype) return 'Select EV type first';
    if (!vehicleType) return 'Select type first';
    return 'Select brand';
  };

  const isDisabled = (vehicleType === 'ev' && !evSubtype) || !vehicleType;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 h-10 text-sm bg-white transition ${
          isDisabled
            ? 'opacity-60 cursor-not-allowed bg-gray-50 border-gray-200'
            : open
            ? 'ring-2 ring-orange-400 border-orange-400'
            : 'border-gray-300 hover:bg-gray-50'
        }`}
      >
        {selected ? (
          <span className="flex items-center gap-2 min-w-0">
            {selected.logo && <img src={selected.logo} alt={selected.name} className="h-5 w-5 object-contain rounded flex-shrink-0" />}
            <span className="font-medium truncate">{selected.name}</span>
          </span>
        ) : (
          <span className="text-gray-400 truncate whitespace-nowrap">{getPlaceholder()}</span>
        )}
        <svg className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && !isDisabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b">
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search brand..."
              className="w-full text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-400"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">
                {search
                  ? `No brands match "${search}"`
                  : vehicleType === 'ev'
                  ? `No EV ${evSubtype} brands found`
                  : 'No brands found for this vehicle type'}
              </p>
            ) : filtered.map(b => (
              <button key={b.id} type="button"
                onClick={() => { onChange(b.id); setOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-orange-50 transition text-left ${value === b.id ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700'}`}>
                {b.logo && <img src={b.logo} alt={b.name} className="h-6 w-6 object-contain rounded flex-shrink-0" />}
                <span className="flex-1">{b.name}</span>
                {/* Show EV sub-category badges */}
                {b.category === 'ev' && Array.isArray(b.ev_subtypes) && b.ev_subtypes.length > 0 && (
                  <span className="ml-auto text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium flex-shrink-0">
                    {b.ev_subtypes.join(' · ')}
                  </span>
                )}
                {value === b.id && (
                  <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── ModelSelect ──────────────────────────────────────────────────────────────
export const ModelSelect = ({ brandId, evSubtype, value, onChange }) => {
  const [models, setModels] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!brandId) { setModels([]); return; }
    const fetch = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ brand_id: brandId, limit: '50' });
        if (evSubtype) params.append('ev_subtype', evSubtype);
        const res = await axios.get(`${API}/models?${params}`);
        setModels(res.data.models || []);
      } catch { setModels([]); }
      finally { setLoading(false); }
    };
    fetch();
    onChange('');
  }, [brandId, evSubtype]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if (!open) setSearch(''); }, [open]);

  const filtered = models.filter(m => !search || m.name?.toLowerCase().includes(search.toLowerCase()));
  const selected = models.find(m => m.id === value);

  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={!brandId}
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-2 border rounded-lg px-3 py-2.5 text-sm bg-white transition ${
          !brandId ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200' : open ? 'ring-2 ring-orange-400 border-orange-400' : 'border-gray-300 hover:bg-gray-50'
        }`}>
        {loading ? (
          <span className="text-gray-400 flex items-center gap-2">
            <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            Loading...
          </span>
        ) : selected ? (
          <span className="font-medium text-gray-800">{selected.name}</span>
        ) : (
          <span className="text-gray-400">{!brandId ? 'Select brand first' : 'Select model'}</span>
        )}
        <svg className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && brandId && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
          <div className="p-2 border-b">
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search model..."
              className="w-full text-sm px-3 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-400" />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-center text-sm text-gray-400 py-6">{search ? `No models match "${search}"` : 'No models found'}</p>
            ) : filtered.map(m => (
              <button key={m.id} type="button"
                onClick={() => { onChange(m.id); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-orange-50 transition text-left ${value === m.id ? 'bg-orange-50 text-orange-700 font-semibold' : 'text-gray-700'}`}>
                {m.name}
                {value === m.id && <svg className="h-4 w-4 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};